import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpParams,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import moment from 'moment';
import { BehaviorSubject, from, Observable, throwError } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { StatusCodeResponseService } from 'src/app/core/services/status-code-response.service';
import { environment } from '../../../environments/environment';
import { AccountService } from '../../core/services/api/account.service';
import { SettingService } from '../../core/services/api/setting.service';
import { StorageService } from '../../core/services/storage.service';
import { PagedResult } from '../models/paged-result/paged-result';
import { HeaderService } from '../services/header/header.service';
import { ReloginModalService } from '../relogin-modal/relogin-modal.service';

@Injectable()
export class HttpResponseInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  // Holds a resolution outcome rather than the raw token (unused downstream anyway) so
  // that a *failed* refresh can also unblock anyone queued in returnRefreshTokenSubject()
  // — see handleSessionExpired(), which is the only other place this now gets notified.
  private refreshTokenSubject: BehaviorSubject<{ success: boolean }> = new BehaviorSubject<{
    success: boolean;
  }>(null);

  // FRC-810: a truly-expired session (refresh also failed, or a flat 401 with no
  // refresh signal at all) used to hard-redirect to /login here, wiping whatever
  // the issuer had typed into the SPA (e.g. the campaign form). Instead we hold the
  // failed request, surface a re-login modal in place, and replay it once re-authed.
  // Same queue/replay shape as isRefreshing/refreshTokenSubject above, so concurrent
  // 401s share one modal instead of opening several.
  private isAwaitingReAuth = false;
  private reAuthSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  constructor(
    private router: Router,
    private injector: Injector,
    private storageService: StorageService,
    private settingService: SettingService,
    private headerService: HeaderService,
    private statusCodeResponseService: StatusCodeResponseService,
  ) {}

  private get accountService(): AccountService {
    return this.injector.get(AccountService);
  }

  private get reloginModalService(): ReloginModalService {
    return this.injector.get(ReloginModalService);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let params = new HttpParams({ fromString: req.params.toString() });

    if (
      (req.url.indexOf(environment.apiServer) != -1 ||
        req.url.indexOf(environment.ieoApiServer) != -1) &&
      !params.get('noToken')
    ) {
      if (this.isRefreshing) {
        return this.returnRefreshTokenSubject(next, req);
      }

      if (!this.storageService.tokenExpiry || this.storageService.tokenExpiry.isBefore(moment())) {
        this.isRefreshing = true;
        this.refreshTokenSubject.next(null);

        return this.returnRefreshTokenObservable(next, req);
      }
    }

    if (params.get('noToken')) {
      params = params.delete('noToken');

      req = req.clone({
        params: params,
        headers: req.headers,
        withCredentials: req.withCredentials,
      });
    }

    return this.mapPagedResult(next.handle(req)).pipe(
      catchError((err: any) => {
        if (err instanceof HttpErrorResponse) {
          if (err.status === 401) {
            if (err.headers.get('token-expired')) {
              return this.handle401Error(req, next);
            } else if (
              err.error &&
              err.error.Error &&
              err.error.Error.Code &&
              err.error.Error.Code == 'TOKEN_EXPIRED_ERROR'
            ) {
              throw err.error;
            } else {
              return this.handleSessionExpired(req, next);
            }
          } else if (err.status === 404 && req.url.indexOf(environment.apiServer) == 0) {
            if (err.error?.error?.code.includes('OPEN404PAGE')) {
              this.headerService.setHeaderTag('pitchIN | 404 Page Not Found');
              this.settingService.notFoundPage.next(true);
              this.statusCodeResponseService.setNotFound();
              return throwError({});
            }
          }

          if (err.error) {
            throw err.error;
          }

          return throwError({});
        }

        return throwError({});
      }),
    );
  }

  private handle401Error(req: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      return this.returnRefreshTokenObservable(next, req);
    } else {
      return this.returnRefreshTokenSubject(next, req);
    }
  }

  private clearTokenAndRedirectToLogin() {
    this.storageService.clear();
    this.accountService.currentUser.next(null);

    const returnUrl = typeof window === 'undefined' ? undefined : window.location?.pathname;
    const queryParams = returnUrl ? { returnUrl } : null;

    this.router.navigate(['/login'], { queryParams }).catch((err) => {
      console.error('fail to navigate to login: ', err);
    });
  }

  // Every retry path re-sends via next.handle(req) directly rather than back through
  // intercept() itself, so each one needs this applied explicitly or a retried
  // paginated request would lose its x-total-count -> PagedResult conversion.
  private mapPagedResult(source: Observable<HttpEvent<any>>): Observable<HttpEvent<any>> {
    return source.pipe(
      map((event) => {
        if (event instanceof HttpResponse && event.headers.get('x-total-count')) {
          return event.clone({
            body: new PagedResult(event.body, parseInt(event.headers.get('x-total-count'))),
          });
        }

        return event;
      }),
    );
  }

  returnRefreshTokenSubject(next: HttpHandler, req: HttpRequest<any>) {
    return this.refreshTokenSubject.pipe(
      filter((result) => result != null),
      take(1),
      switchMap((result) =>
        result.success
          ? this.mapPagedResult(next.handle(req))
          : throwError({ error: { message: 'common.sessionExpired' } }),
      ),
    );
  }

  returnRefreshTokenObservable(next: HttpHandler, req: HttpRequest<any>) {
    return this.accountService.refreshToken().pipe(
      switchMap((token: any) => {
        this.isRefreshing = false;
        this.refreshTokenSubject.next({ success: true });

        return this.mapPagedResult(next.handle(req));
      }),
      catchError((err) => {
        this.isRefreshing = false;

        return this.handleSessionExpired(req, next);
      }),
    );
  }

  // Holds the request that triggered a genuine session expiry (refresh failed, or a
  // flat 401 with no refresh signal), shows one shared re-login modal, and replays the
  // request once re-authed. Cancelling the modal (or a re-auth failure) falls back to
  // the previous hard-redirect behavior — the one case data loss is unavoidable, per
  // the ticket's documented edge cases.
  private handleSessionExpired(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (this.isAwaitingReAuth) {
      return this.reAuthSubject.pipe(
        filter((resolved) => resolved !== null),
        take(1),
        switchMap((success) =>
          success
            ? this.mapPagedResult(next.handle(req))
            : throwError({ error: { message: 'common.sessionExpired' } }),
        ),
      );
    }

    this.isAwaitingReAuth = true;
    this.reAuthSubject.next(null);

    // catchError only wraps the modal itself (cancel / 2FA bail-out) — a failure from
    // the replayed request afterwards is unrelated to re-auth and must propagate as-is,
    // not be mistaken for a failed login and trigger a redirect.
    return from(this.reloginModalService.open()).pipe(
      catchError(() => {
        this.isAwaitingReAuth = false;
        this.reAuthSubject.next(false);
        // A refreshToken() failure routes here too, so anyone still parked in
        // returnRefreshTokenSubject() from that same failed refresh cycle needs
        // releasing as well — otherwise, now that we no longer navigate away on
        // failure, they'd wait on a refreshTokenSubject that never resolves again.
        this.refreshTokenSubject.next({ success: false });
        this.clearTokenAndRedirectToLogin();

        return throwError({ error: { message: 'common.sessionExpired' } });
      }),
      switchMap(() => {
        this.isAwaitingReAuth = false;
        this.reAuthSubject.next(true);
        this.refreshTokenSubject.next({ success: true });

        return this.mapPagedResult(next.handle(req));
      }),
    );
  }
}
